import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAniList } from '../context/AniListContext';
import { useMangaSource } from '../context/MangaSourceContext';
import { useLinkSettings } from '../context/LinkSettingsContext';
import { useUndoRedo } from '../context/UndoRedoContext';
import { useAlert } from '../context/ToastContext';
import { formatChapterProgress, formatVolumeProgress, getTotalChapters, getTotalVolumes, calculateMangaProgress, getMangaMediaStatusBadge } from '../utils/timeFormatter';
import { formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { updateMangaProgress, updateMangaVolumeProgress, updateAnimeStatus, updateMediaScore } from '../services/aniListService';
import { getSimpleCache } from '../services/cacheService';
import { LOCAL_STORAGE_KEYS } from '../config/constants';
import StatusButton from './StatusButton';
import EpisodeCounter from './EpisodeCounter';
import ConfirmationModal from './ConfirmationModal';
import GroupStatusModal from './GroupStatusModal';
import SiteLinksModal from './SiteLinksModal';
import ScoreInput from './ScoreInput';
import { CollectionsSkeleton } from './common/SkeletonLoader';
import './Collections.css';

const SORT_OPTIONS = [
    { id: 'name', label: '📝 Name', sortFn: (a, b, dir) => dir * a.name.localeCompare(b.name) },
    { id: 'averageScore', label: '⭐ Average Score', sortFn: (a, b, dir) => dir * (b.stats.averageScore - a.stats.averageScore) },
    { id: 'totalManga', label: '📚 Total Manga', sortFn: (a, b, dir) => dir * ((b.stats.mangaCount || b.stats.animeCount) - (a.stats.mangaCount || a.stats.animeCount)) },
    { id: 'totalChapters', label: '📖 Total Chapters', sortFn: (a, b, dir) => dir * (b.stats.totalChapters - a.stats.totalChapters) },
    { id: 'readChapters', label: '✅ Read Chapters', sortFn: (a, b, dir) => dir * (b.stats.readChapters - a.stats.readChapters) },
    { id: 'remainingChapters', label: '⏳ Remaining Chapters', sortFn: (a, b, dir) => dir * (b.stats.remainingChapters - a.stats.remainingChapters) }
];

const MANGA_COLLECTIONS_SORT_BY_KEY = 'manga_collections_sort_by';
const MANGA_COLLECTIONS_SORT_DIR_KEY = 'manga_collections_sort_direction';

const MangaCollections = () => {
    const location = useLocation();
    const { titlePreference } = useTitlePreference();
    const { token } = useAniList();
    const { mangaGroups, isLoading, refreshData, latestChapterMap } = useMangaSource();
    const { readLinks } = useLinkSettings();
    const { addChange, undo, redo, canUndo, canRedo } = useUndoRedo();
    const { showError, showWarning } = useAlert();

    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [matchedManga, setMatchedManga] = useState(new Set());
    const [sortBy, setSortBy] = useState(() => {
        return localStorage.getItem(MANGA_COLLECTIONS_SORT_BY_KEY) || 'name';
    });
    const [sortDirection, setSortDirection] = useState(() => {
        return localStorage.getItem(MANGA_COLLECTIONS_SORT_DIR_KEY) || 'asc';
    });
    const [showRebuildConfirm, setShowRebuildConfirm] = useState(false);
    const [changingStatus, setChangingStatus] = useState(new Set());
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [bulkProgress, setBulkProgress] = useState(null);
    const [optimisticProgress, setOptimisticProgress] = useState(new Map());
    const [updatingProgress, setUpdatingProgress] = useState(new Set());
    const [optimisticVolumes, setOptimisticVolumes] = useState(new Map());
    const [updatingVolumes, setUpdatingVolumes] = useState(new Set());
    const [optimisticScores, setOptimisticScores] = useState(new Map());
    const [updatingScores, setUpdatingScores] = useState(new Set());
    const [showReadModal, setShowReadModal] = useState(false);
    const [selectedMangaForModal, setSelectedMangaForModal] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPages, setLoadedPages] = useState(new Set([1]));

    const GROUPS_PER_PAGE = 15;

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        localStorage.setItem(MANGA_COLLECTIONS_SORT_BY_KEY, sortBy);
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem(MANGA_COLLECTIONS_SORT_DIR_KEY, sortDirection);
    }, [sortDirection]);

    // Filter and sort groups
    const filteredGroups = useMemo(() => {
        if (!mangaGroups || mangaGroups.length === 0) return [];

        let groups = mangaGroups;
        const matched = new Set();

        if (debouncedSearchQuery.trim()) {
            const query = debouncedSearchQuery.toLowerCase();
            const queryNoSpaces = query.replace(/\s+/g, '');
            groups = groups.filter(group => {
                const groupNameMatches = group.name.toLowerCase().includes(query) || group.name.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces);
                const entries = group.anime || group.manga || [];
                const matchingManga = entries.filter(entry => {
                    const englishTitle = entry.media.title.english || '';
                    const romanjiTitle = entry.media.title.romaji || '';
                    return englishTitle.toLowerCase().includes(query) ||
                        romanjiTitle.toLowerCase().includes(query) ||
                        englishTitle.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces) ||
                        romanjiTitle.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces);
                });
                matchingManga.forEach(entry => matched.add(entry.media.id));
                return groupNameMatches || matchingManga.length > 0;
            });
        }

        setMatchedManga(matched);

        const sortOption = SORT_OPTIONS.find(opt => opt.id === sortBy);
        if (sortOption) {
            const direction = sortDirection === 'asc' ? 1 : -1;
            groups = [...groups].sort((a, b) => sortOption.sortFn(a, b, direction));
        }

        return groups;
    }, [mangaGroups, debouncedSearchQuery, sortBy, sortDirection]);

    // Paginated groups
    const displayedGroups = useMemo(() => {
        let groupsToShow = [];
        Array.from(loadedPages).sort((a, b) => a - b).forEach(page => {
            const startIndex = (page - 1) * GROUPS_PER_PAGE;
            const endIndex = startIndex + GROUPS_PER_PAGE;
            groupsToShow.push(...filteredGroups.slice(startIndex, endIndex));
        });
        return groupsToShow;
    }, [filteredGroups, loadedPages]);

    const totalPages = Math.ceil(filteredGroups.length / GROUPS_PER_PAGE);

    useEffect(() => {
        setCurrentPage(1);
        setLoadedPages(new Set([1]));
    }, [debouncedSearchQuery, sortBy, sortDirection]);

    useEffect(() => {
        if (currentPage === 1 && totalPages > 1 && !loadedPages.has(2)) {
            const timer = setTimeout(() => {
                setLoadedPages(prev => new Set([...prev, 2]));
                setCurrentPage(2);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [currentPage, totalPages, loadedPages]);

    const loadNextPage = useCallback(() => {
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages && !loadedPages.has(nextPage)) {
            setLoadedPages(prev => new Set([...prev, nextPage]));
            setCurrentPage(nextPage);
        }
    }, [currentPage, totalPages, loadedPages]);

    const handleScroll = useCallback(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const pageHeight = document.documentElement.scrollHeight;
        const triggerHeight = pageHeight - 1000;
        if (scrollPosition >= triggerHeight && currentPage < totalPages && !loadedPages.has(currentPage + 1)) {
            loadNextPage();
        }
    }, [currentPage, totalPages, loadedPages, loadNextPage]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const toggleGroup = useCallback((groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    }, [expandedGroups]);

    // Handle score change
    const handleScoreChange = async (mediaId, newScoreRaw, mangaTitle) => {
        const tkn = token || getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }
        setOptimisticScores(prev => new Map(prev).set(mediaId, newScoreRaw));
        setUpdatingScores(prev => new Set([...prev, mediaId]));
        try {
            await updateMediaScore(mediaId, newScoreRaw, tkn);
            if (refreshData) refreshData(true);
        } catch (error) {
            showError(`Failed to update score for ${mangaTitle}`, 'Score Update Failed');
            setOptimisticScores(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingScores(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle chapter progress change
    const handleProgressChange = async (mediaId, newProgress, totalChapters, mangaTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newProgress);
        if (totalChapters > 0) {
            clamped = Math.min(clamped, totalChapters);
        } else if (totalChapters === 0) {
            clamped = 0;
        }

        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        setOptimisticProgress(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingProgress(prev => new Set([...prev, mediaId]));

        try {
            await updateMangaProgress(mediaId, clamped, token);
            console.log(`✅ Updated ${mangaTitle} chapter progress to ${clamped}`);
            if (refreshData) {
                refreshData(true);
            }

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Reading)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${mangaTitle} — progress set to ${clamped}, switching to Reading`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', mangaTitle);
            }

            // Auto-complete: if all chapters read and manga is finished, mark as COMPLETED
            if (clamped > 0 && totalChapters > 0 && clamped === totalChapters && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${mangaTitle} — all ${totalChapters} chapters read`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', mangaTitle);
            }
        } catch (error) {
            console.error(`❌ Failed to update progress for ${mangaTitle}:`, error);
            showError(`Failed to update progress for ${mangaTitle}`, 'Update Failed');
            setOptimisticProgress(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });
        } finally {
            setUpdatingProgress(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        }
    };

    // Handle volume progress change
    const handleVolumeProgress = async (mediaId, newVolumes, totalVolumes, mangaTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newVolumes);
        if (totalVolumes > 0) clamped = Math.min(clamped, totalVolumes);

        if (!token) {
            showError('Authentication required.', 'Auth Required');
            return;
        }

        setOptimisticVolumes(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingVolumes(prev => new Set([...prev, mediaId]));

        try {
            await updateMangaVolumeProgress(mediaId, clamped, token);
            if (refreshData) refreshData(true);

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Reading)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${mangaTitle} — volume progress set to ${clamped}, switching to Reading`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', mangaTitle);
            }

            // Auto-complete: if all volumes read and manga is finished, mark as COMPLETED
            if (clamped > 0 && totalVolumes > 0 && clamped === totalVolumes && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${mangaTitle} — all ${totalVolumes} volumes read`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', mangaTitle);
            }
        } catch (error) {
            showError(`Failed to update volume for ${mangaTitle}`, 'Update Failed');
            setOptimisticVolumes(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingVolumes(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle manga status change
    const handleStatusChange = async (mediaId, currentStatus, newStatus, mangaTitle) => {
        if (newStatus === currentStatus) return;
        if (!token) {
            showError('Authentication required.', 'Auth Required');
            return;
        }

        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        addChange({
            type: 'STATUS_CHANGE',
            mediaId,
            animeTitle: mangaTitle,
            oldStatus: currentStatus,
            newStatus,
            timestamp: Date.now()
        });

        try {
            await updateAnimeStatus(mediaId, newStatus, token);
            if (refreshData) refreshData(true);
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        } catch (error) {
            setOptimisticUpdates(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');
        }
    };

    // Handle undo
    const handleUndo = useCallback(async () => {
        const change = undo();
        if (!change) return;

        const tkn = token || getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);

        if (change.type === 'BULK_STATUS_CHANGE') {
            const { changes, groupName } = change;
            setBulkProgress({ total: changes.length, completed: 0, failed: 0 });

            for (const c of changes) {
                setOptimisticUpdates(prev => new Map(prev).set(c.mediaId, c.oldStatus));
                setChangingStatus(prev => new Set([...prev, c.mediaId]));
                try {
                    await updateAnimeStatus(c.mediaId, c.oldStatus, tkn);
                    setChangingStatus(prev => { const n = new Set(prev); n.delete(c.mediaId); return n; });
                } catch (error) {
                    setOptimisticUpdates(prev => { const n = new Map(prev); n.delete(c.mediaId); return n; });
                    setChangingStatus(prev => { const n = new Set(prev); n.delete(c.mediaId); return n; });
                }
                setBulkProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
            }
            setTimeout(() => setBulkProgress(null), 2000);
            return;
        }

        const { mediaId, oldStatus, animeTitle } = change;
        setOptimisticUpdates(prev => new Map(prev).set(mediaId, oldStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));
        try {
            await updateAnimeStatus(mediaId, oldStatus, tkn);
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        } catch (error) {
            setOptimisticUpdates(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
            showError(`Failed to undo: ${error.message}`, 'Undo Failed');
        }
    }, [undo, token]);

    // Handle redo
    const handleRedo = useCallback(async () => {
        const change = redo();
        if (!change) return;

        const tkn = token || getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);

        if (change.type === 'BULK_STATUS_CHANGE') {
            const { changes, groupName } = change;
            setBulkProgress({ total: changes.length, completed: 0, failed: 0 });

            for (const c of changes) {
                setOptimisticUpdates(prev => new Map(prev).set(c.mediaId, c.newStatus));
                setChangingStatus(prev => new Set([...prev, c.mediaId]));
                try {
                    await updateAnimeStatus(c.mediaId, c.newStatus, tkn);
                    setChangingStatus(prev => { const n = new Set(prev); n.delete(c.mediaId); return n; });
                } catch (error) {
                    setOptimisticUpdates(prev => { const n = new Map(prev); n.delete(c.mediaId); return n; });
                    setChangingStatus(prev => { const n = new Set(prev); n.delete(c.mediaId); return n; });
                }
                setBulkProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
            }
            setTimeout(() => setBulkProgress(null), 2000);
            return;
        }

        const { mediaId, newStatus, animeTitle } = change;
        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));
        try {
            await updateAnimeStatus(mediaId, newStatus, tkn);
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        } catch (error) {
            setOptimisticUpdates(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
            showError(`Failed to redo: ${error.message}`, 'Redo Failed');
        }
    }, [redo, token]);

    // Handle bulk status change
    const handleBulkStatusChange = useCallback(async (groupId, mode, fromStatus, toStatus) => {
        const group = mangaGroups.find(g => g.id === groupId);
        if (!group) return;

        let mangaToChange;
        if (mode === 'all') {
            mangaToChange = group.anime;
        } else {
            mangaToChange = group.anime.filter(a => a.status === fromStatus);
        }

        if (mangaToChange.length === 0) {
            showWarning('No manga match the criteria', 'No Matches');
            setBulkModalOpen(false);
            return;
        }

        const tkn = token || getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) {
            showError('Authentication required.', 'Auth Required');
            return;
        }

        setBulkModalOpen(false);

        setOptimisticUpdates(prev => {
            const next = new Map(prev);
            mangaToChange.forEach(m => next.set(m.media.id, toStatus));
            return next;
        });

        if (refreshData) refreshData(true);

        setBulkProgress({ total: mangaToChange.length, completed: 0, failed: 0 });

        const changes = mangaToChange.map(m => ({
            mediaId: m.media.id,
            animeTitle: getDisplayTitle(m.media, titlePreference),
            oldStatus: m.status,
            newStatus: toStatus
        }));

        addChange({
            type: 'BULK_STATUS_CHANGE',
            groupId,
            groupName: group.name,
            changes,
            timestamp: Date.now()
        });

        const errors = [];
        for (let i = 0; i < mangaToChange.length; i += 3) {
            const batch = mangaToChange.slice(i, i + 3);
            const results = await Promise.allSettled(
                batch.map(m => updateAnimeStatus(m.media.id, toStatus, tkn))
            );
            results.forEach((result, idx) => {
                if (result.status === 'rejected') errors.push(batch[idx].media.id);
            });
            setBulkProgress(prev => ({
                ...prev,
                completed: Math.min(i + batch.length, mangaToChange.length),
                failed: errors.length
            }));
            if (i + 3 < mangaToChange.length) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        if (refreshData) refreshData(true);
        setTimeout(() => setBulkProgress(null), 2000);
    }, [mangaGroups, refreshData, token]);

    const openBulkModal = useCallback((groupId) => {
        const group = mangaGroups.find(g => g.id === groupId);
        setSelectedGroup(group);
        setBulkModalOpen(true);
    }, [mangaGroups]);

    // Handle URL parameter for auto-expanding
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const expandGroupId = searchParams.get('expandGroup');
        if (expandGroupId && mangaGroups && mangaGroups.length > 0) {
            setExpandedGroups(new Set([expandGroupId]));
            setTimeout(() => {
                const element = document.getElementById(`group-${expandGroupId}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'auto', block: 'center' });
                }
            }, 100);
        }
    }, [location.search, mangaGroups]);

    const getStatusBadge = (status) => {
        const badges = {
            'CURRENT': { emoji: '▶️', label: 'Reading', color: '#4CAF50' },
            'COMPLETED': { emoji: '✅', label: 'Completed', color: '#2196F3' },
            'PLANNING': { emoji: '📅', label: 'Planning', color: '#FF9800' },
            'PAUSED': { emoji: '⏸️', label: 'On Hold', color: '#9E9E9E' },
            'DROPPED': { emoji: '❌', label: 'Dropped', color: '#F44336' },
            'REPEATING': { emoji: '🔄', label: 'Rereading', color: '#9C27B0' }
        };
        const badge = badges[status] || { emoji: '❓', label: status, color: '#757575' };
        return (
            <span className="status-badge" style={{ backgroundColor: badge.color }}>
                {badge.emoji} {badge.label}
            </span>
        );
    };

    if (isLoading && (!mangaGroups || mangaGroups.length === 0)) {
        return <CollectionsSkeleton />;
    }

    if (!mangaGroups || mangaGroups.length === 0) {
        return (
            <div className="groups-container">
                <div className="empty-state">
                    <div className="empty-icon">📖</div>
                    <h3>No Manga Groups Yet</h3>
                    <p>Groups will be built automatically from your manga list.</p>
                    <p>Make sure you're connected to AniList.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="groups-container">
            <div className="groups-header">
                <h1>📖 manga groups</h1>
                <p>Your manga organized by relation groups</p>
                <div className="header-buttons">
                    <button
                        onClick={() => setShowRebuildConfirm(true)}
                        className="force-rebuild-btn"
                        title="Clear cache and rebuild groups"
                    >
                        🔄 Force Rebuild Groups
                    </button>

                    <div className="undo-redo-buttons">
                        <button
                            onClick={handleUndo}
                            disabled={!canUndo}
                            className="undo-btn"
                            title="Undo last status change"
                        >
                            ↩️ Undo
                        </button>
                        <button
                            onClick={handleRedo}
                            disabled={!canRedo}
                            className="redo-btn"
                            title="Redo last undone change"
                        >
                            ↪️ Redo
                        </button>
                    </div>
                </div>

                <ConfirmationModal
                    isOpen={showRebuildConfirm}
                    onConfirm={() => {
                        if (refreshData) refreshData(true);
                        setShowRebuildConfirm(false);
                    }}
                    onCancel={() => setShowRebuildConfirm(false)}
                    title="Rebuild Manga Collections?"
                    message="This will clear the cached data and fetch fresh information from AniList to rebuild all your manga groups."
                    confirmText="Rebuild Now"
                    cancelText="Cancel"
                />
            </div>

            {/* Search and Sort Bar */}
            <div className="search-sort-container">
                <input
                    type="text"
                    className="search-input"
                    placeholder="🔍 Search groups or manga..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="sort-controls">
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                        title="Sort by"
                    >
                        {SORT_OPTIONS.map(option => (
                            <option key={option.id} value={option.id}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                        className="sort-direction-btn"
                        title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDirection === 'asc' ? '↑' : '↓'}
                    </button>
                </div>
            </div>

            {/* Groups List */}
            <div className="groups-list">
                {filteredGroups.length === 0 ? (
                    <div className="empty-state">
                        <p>No groups found for this filter{searchQuery && ` matching "${searchQuery}"`}.</p>
                        {searchQuery && (
                            <p style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '4px' }}>Check the spelling or try different keywords</p>
                        )}
                    </div>
                ) : (
                    <>
                        {displayedGroups.map(group => {
                            const isExpanded = expandedGroups.has(group.id);
                            const oldestManga = group.anime[0];
                            const coverImage = oldestManga?.media?.coverImage?.extraLarge || oldestManga?.media?.coverImage?.large || oldestManga?.media?.coverImage?.medium;
                            const groupName = getDisplayTitle(oldestManga?.media, titlePreference);
                            const hasReleasing = group.anime.some(a => a.media?.status === 'RELEASING');

                            return (
                                <div key={group.id} id={`group-${group.id}`} className={`group-card ${isExpanded ? 'expanded' : ''}`}>
                                    {/* Group Header */}
                                    <div
                                        className="group-header"
                                        onClick={() => toggleGroup(group.id)}
                                    >
                                        {coverImage && (
                                            <img
                                                src={coverImage}
                                                alt={groupName}
                                                className="group-cover"
                                            />
                                        )}
                                        <div className="group-info">
                                            <h3 className="group-name">
                                                {isExpanded ? '📖' : '📚'} {groupName}
                                            </h3>
                                            {hasReleasing && <span className="group-releasing-tag">📡 Publishing</span>}
                                            <div className="group-stats">
                                                <span className="stat-anime-count">{group.stats.mangaCount || group.stats.animeCount} manga</span>
                                                <span>•</span>
                                                <span className="stat-episodes">
                                                    📖 {formatChapterProgress(group.stats.readChapters, group.stats.totalChapters)} ch
                                                </span>
                                                {group.stats.totalVolumes > 0 && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="stat-episodes">
                                                            📕 {formatVolumeProgress(group.stats.readVolumes, group.stats.totalVolumes)} vol
                                                        </span>
                                                    </>
                                                )}
                                                {group.stats.remainingChapters > 0 && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="stat-remaining-time">⏳ {group.stats.remainingChapters} ch{group.stats.remainingVolumes > 0 ? ` / ${group.stats.remainingVolumes} vol` : ''}</span>
                                                    </>
                                                )}
                                                {group.stats.averageScore > 0 && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="stat-score">⭐ {(group.stats.averageScore / 10).toFixed(1)}</span>
                                                    </>
                                                )}

                                            </div>
                                        </div>
                                        <button
                                            className="bulk-status-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openBulkModal(group.id);
                                            }}
                                            title="Change status for all manga in group"
                                        >
                                            ⚙️ Bulk
                                        </button>
                                        <button className="expand-btn">
                                            {isExpanded ? '▼' : '▶'}
                                        </button>
                                    </div>

                                    {/* Group Content (Expanded) */}
                                    {isExpanded && (
                                        <div className="group-content">
                                            <div className="anime-grid">
                                                {group.anime.map((entry, index) => {
                                                    const title = getDisplayTitle(entry.media, titlePreference);
                                                    const releaseDate = formatReleaseDate(entry.media.startDate);
                                                    const totalChapters = getTotalChapters(entry, latestChapterMap);
                                                    const readChapters = entry.progress || 0;
                                                    const totalVolumes = getTotalVolumes(entry);
                                                    const readVolumes = entry.progressVolumes || 0;
                                                    const displayStatus = optimisticUpdates.get(entry.media.id) || entry.status;
                                                    const mangaProgress = calculateMangaProgress(entry, latestChapterMap);
                                                    const readChaptersDisplay = optimisticProgress.has(entry.media.id) ? optimisticProgress.get(entry.media.id) : readChapters;

                                                    return (
                                                        <div key={entry.id} className="anime-item">
                                                            <div className="anime-cover-wrapper">
                                                                <div className="anime-index">{index + 1}</div>
                                                                <img
                                                                    src={entry.media.coverImage?.extraLarge || entry.media.coverImage?.large || entry.media.coverImage?.medium}
                                                                    alt={title}
                                                                    className="anime-cover"
                                                                />
                                                            </div>
                                                            <div className="anime-details">
                                                                <h4 className="anime-title">
                                                                    <a
                                                                        href={`https://anilist.co/manga/${entry.media.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {title}
                                                                    </a>
                                                                </h4>
                                                                <div className="anime-action-buttons">
                                                                    <ScoreInput
                                                                        mediaId={entry.media.id}
                                                                        currentScore={optimisticScores.has(entry.media.id) ? optimisticScores.get(entry.media.id) : (entry.score || 0)}
                                                                        isUpdating={updatingScores.has(entry.media.id)}
                                                                        onChange={(newScore) => handleScoreChange(entry.media.id, newScore, title)}
                                                                    />
                                                                    <EpisodeCounter
                                                                        mediaId={entry.media.id}
                                                                        currentProgress={readChaptersDisplay}
                                                                        totalEpisodes={totalChapters}
                                                                        isUpdating={updatingProgress.has(entry.media.id)}
                                                                        onChange={(newVal) => handleProgressChange(entry.media.id, newVal, totalChapters, title, entry.media.status, optimisticUpdates.get(entry.media.id) || entry.status)}
                                                                        label="chapter"
                                                                    />
                                                                    {totalVolumes > 0 && (
                                                                        <EpisodeCounter
                                                                            mediaId={`vol-${entry.media.id}`}
                                                                            currentProgress={optimisticVolumes.has(entry.media.id) ? optimisticVolumes.get(entry.media.id) : readVolumes}
                                                                            totalEpisodes={totalVolumes}
                                                                            isUpdating={updatingVolumes.has(entry.media.id)}
                                                                            onChange={(newVal) => handleVolumeProgress(entry.media.id, newVal, totalVolumes, title, entry.media.status, optimisticUpdates.get(entry.media.id) || entry.status)}
                                                                            label="volume"
                                                                        />
                                                                    )}
                                                                    <StatusButton
                                                                        currentStatus={optimisticUpdates.get(entry.media.id) || entry.status}
                                                                        onChange={(newStatus) => handleStatusChange(entry.media.id, entry.status, newStatus, title)}
                                                                        disabled={changingStatus.has(entry.media.id)}
                                                                        isLoading={changingStatus.has(entry.media.id)}
                                                                        type="manga"
                                                                    />
                                                                    <button
                                                                        className="action-btn watch-btn"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedMangaForModal(title);
                                                                            setShowReadModal(true);
                                                                        }}
                                                                        title="Read"
                                                                    >
                                                                        📖
                                                                    </button>
                                                                </div>
                                                                <div className="anime-meta">
                                                                    <span>{releaseDate}</span>
                                                                    <span>{entry.media.format}</span>
                                                                    <span>📖 {readChapters}/{totalChapters || '?'} ch</span>
                                                                    {totalVolumes > 0 && <span>📕 {readVolumes}/{totalVolumes} vol</span>}
                                                                    {entry.media.averageScore && <span>⭐ {(entry.media.averageScore / 10).toFixed(1)}</span>}
                                                                    {mangaProgress.remainingChapters > 0 && (
                                                                        <span>⏳ {mangaProgress.remainingChapters} ch{mangaProgress.remainingVolumes > 0 ? ` / ${mangaProgress.remainingVolumes} vol` : ''}</span>
                                                                    )}
                                                                    {(() => { const badge = getMangaMediaStatusBadge(entry.media?.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {/* Load More Button */}
                        {loadedPages.size < totalPages && (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <button
                                    onClick={loadNextPage}
                                    className="force-rebuild-btn"
                                    style={{ margin: '0 auto' }}
                                >
                                    📥 Load More ({displayedGroups.length} / {filteredGroups.length})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Summary Footer */}
            <div className="groups-summary">
                <p>
                    Showing {displayedGroups.length} of {filteredGroups.length} groups (Total: {mangaGroups.length})
                    {searchQuery && ` for "${searchQuery}"`}
                    {loadedPages.size < totalPages && ` • Page ${loadedPages.size} of ${totalPages}`}
                </p>
            </div>

            {/* Read Modal */}
            <SiteLinksModal
                isOpen={showReadModal}
                onClose={() => {
                    setShowReadModal(false);
                    setSelectedMangaForModal(null);
                }}
                links={readLinks}
                animeName={selectedMangaForModal || ''}
                type="read"
            />

            {/* Bulk Status Change Modal */}
            <GroupStatusModal
                isOpen={bulkModalOpen}
                onClose={() => setBulkModalOpen(false)}
                group={selectedGroup}
                onApply={handleBulkStatusChange}
                type="manga"
            />

            {/* Bulk Progress Bar */}
            {bulkProgress && (
                <div className="bulk-progress-bar" style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    padding: '15px 20px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    zIndex: 1000,
                    minWidth: '250px'
                }}>
                    <strong>🔄 Updating Status</strong>
                    <div style={{ marginTop: '8px' }}>
                        Progress: {bulkProgress.completed}/{bulkProgress.total}
                        {bulkProgress.failed > 0 && ` (❌ ${bulkProgress.failed} failed)`}
                    </div>
                    <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        borderRadius: '3px',
                        marginTop: '8px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${Math.round((bulkProgress.completed / bulkProgress.total) * 100)}%`,
                            height: '100%',
                            backgroundColor: '#4CAF50',
                            transition: 'width 0.3s ease'
                        }}></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(MangaCollections);
